import { useState, useEffect } from 'react'
import { avatarGradient, initialOf } from '../lib/avatars'

// ── Avatar ────────────────────────────────────────────────────────────────────
// One premium avatar everywhere: a real photo if there is one, else the person's
// initial on their per-name two-tone gradient, with a soft top-light/bottom-shadow
// so it reads dimensional. The gradient is deterministic (same person → same color
// on every surface, no assets, no upload), so you still recognize people by hue —
// but the letter makes "who is this" instant (Jason 2026-07-07: the geometric
// identicons were pretty but hard to tell apart).
// `frame`: an optional { color1, color2, glow } tier object (see useStreakFrame's
// TIERS) drawing a second, thicker ring OUTSIDE the existing contextual `ring` —
// the first real reward hung off something other than the ladder (2026-09-18).
// A two-tone gradient border reads as "earned" rather than just "colored," and a
// small glow keeps it visible on both light and dark surfaces.
export function Avatar({ name, avatarUrl, size = 40, ring, frame, style }) {
  const shadows = [
    ring ? `0 0 0 2px ${ring}` : null,
    frame ? `0 0 0 ${ring ? 4 : 2}px var(--bg), 0 0 0 ${ring ? 7 : 5}px ${frame.color1}` : null,
  ].filter(Boolean).join(', ')
  const common = {
    width: size, height: size, borderRadius: 'var(--radius-full)', flexShrink: 0,
    ...(shadows ? { boxShadow: shadows } : {}),
    ...(frame ? { filter: `drop-shadow(0 0 4px ${frame.glow})` } : {}),
    ...style,
  }
  // A dead avatar URL used to render the browser's broken-image glyph with the name as
  // alt text beside it (profile page, simulator walk 2026-10-25). Fall back to the
  // initial on error, and forget the failure if the URL changes.
  const [broken, setBroken] = useState(false)
  useEffect(() => { setBroken(false) }, [avatarUrl])
  if (avatarUrl && !broken) {
    return <img src={avatarUrl} alt={name || ''} draggable="false" onError={() => setBroken(true)}
      style={{ ...common, objectFit: 'cover', display: 'block', pointerEvents: 'none' }} />
  }
  return (
    <div aria-hidden="true" style={{
      ...common,
      background: avatarGradient(name),
      position: 'relative', overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontFamily: 'var(--font)', fontWeight: 700, lineHeight: 1,
      fontSize: Math.round(size * 0.42), letterSpacing: '0.01em',
      textShadow: '0 1px 2px rgba(0,0,0,0.28)', userSelect: 'none',
      boxShadow: `${shadows ? `${shadows}, ` : ''}inset 0 2px 2px rgba(var(--ink-rgb),0.28), inset 0 -3px 4px rgba(0,0,0,0.22)`,
    }}>
      {initialOf(name)}
    </div>
  )
}
